import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLang } from '../i18n/translations';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { WeatherWidget } from '../components/WeatherWidget';
import { ShareButtons } from '../components/ShareButtons';
import { toast } from 'sonner';
import {
  MapPin, Phone, Mail, Globe, Navigation, CalendarCheck, ChevronLeft, Send
} from 'lucide-react';

export const navyPin = L.divIcon({
  className: 'custom-pin',
  html: `<svg width="34" height="34" viewBox="0 0 24 24" fill="#001752" stroke="#fff" stroke-width="1"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#fff"/></svg>`,
  iconSize: [34, 34], iconAnchor: [17, 34]
});

export default function PonudaDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const [offer, setOffer] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [cat, setCat] = useState(null);
  const [type, setType] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: o } = await api.get(`/offers/${id}`);
        setOffer(o);
        const [cats, types] = await Promise.all([api.get('/categories'), api.get('/types')]);
        setCat(cats.data.find((c) => c.id === o.categoryId) || null);
        setType(types.data.find((x) => x.id === o.typeId) || null);
      } catch (err) {
        setOffer(null);
      }
      setActiveImg(0);
      window.scrollTo(0, 0);
    };
    load();
  }, [id]);

  if (!offer) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-500">
        Lokacija nije pronađena. <Link to="/ponuda" className="text-[#001752] font-semibold underline">Nazad na ponudu</Link>
      </div>
    );
  }

  const sendInquiry = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Molimo popunite sva polja.');
      return;
    }
    setSending(true);
    api.post('/inquiries', { ...form, offerId: offer.id, offerTitle: offer.title })
      .then(() => {
        toast.success('Upit je uspješno poslan! Odgovorit ćemo vam u najkraćem roku.');
        setForm({ name: '', email: '', message: '' });
      })
      .catch(() => toast.error('Greška pri slanju upita. Pokušajte ponovo.'))
      .finally(() => setSending(false));
  };

  return (
    <div className="bg-[#f8f9fd] min-h-screen pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/ponuda" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#001752] transition-colors duration-200 mb-6" data-testid="back-to-ponuda">
          <ChevronLeft className="h-4 w-4" /> {t('offer')}
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* GLAVNI DIO */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="h-[420px] overflow-hidden">
                <img src={offer.gallery[activeImg] || offer.image} alt={offer.title} className="w-full h-full object-cover" data-testid="detail-main-image" />
              </div>
              <div className="flex gap-2 p-3">
                {offer.gallery.map((g, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                    className={`h-20 w-28 rounded-lg overflow-hidden border-2 transition-colors duration-200 ${i === activeImg ? 'border-[#001752]' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-7">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {type && <Badge className="bg-[#e8ecf7] text-[#001752] hover:bg-[#e8ecf7]">{type.name}</Badge>}
                {cat && <Badge variant="outline" className="border-slate-200 text-slate-600">{cat.name}</Badge>}
              </div>
              <h1 className="text-3xl font-bold text-[#0b1a3d] mb-2" data-testid="detail-title">{offer.title}</h1>
              <p className="flex items-center gap-1.5 text-slate-500 text-sm mb-6">
                <MapPin className="h-4 w-4" /> {offer.address}, {offer.city}
              </p>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line" data-testid="detail-description">{offer.description}</p>
              <div className="mt-6 pt-5 border-t border-slate-100">
                <ShareButtons url={`${window.location.origin}/ponuda/${offer.id}`} text={offer.title} />
              </div>
            </div>

            {/* UPIT FORMA */}
            <div className="bg-white rounded-xl border border-slate-100 p-7" data-testid="inquiry-form">
              <h2 className="text-xl font-bold text-[#0b1a3d] mb-5">{t('sendInquiry')}</h2>
              <form onSubmit={sendInquiry} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input placeholder={t('name')} value={form.name} data-testid="inquiry-name"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-slate-200" />
                  <Input type="email" placeholder={t('email')} value={form.email} data-testid="inquiry-email"
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-200" />
                </div>
                <Textarea placeholder={t('message')} rows={4} value={form.message} data-testid="inquiry-message"
                  onChange={(e) => setForm({ ...form, message: e.target.value })} className="border-slate-200" />
                <Button type="submit" disabled={sending} data-testid="inquiry-submit"
                  className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
                  <Send className="h-4 w-4 mr-2" /> {sending ? '...' : t('send')}
                </Button>
              </form>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="font-bold text-[#0b1a3d] mb-4">{t('contact')}</h3>
              <div className="space-y-3 text-sm">
                {offer.phone && <p className="flex items-center gap-2.5 text-slate-600"><Phone className="h-4 w-4 text-[#001752]" /> {offer.phone}</p>}
                {offer.email && <p className="flex items-center gap-2.5 text-slate-600 break-all"><Mail className="h-4 w-4 text-[#001752]" /> {offer.email}</p>}
                {offer.website && (
                  <a href={offer.website} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-[#001752] hover:underline">
                    <Globe className="h-4 w-4" /> {offer.website.replace('https://', '')}
                  </a>
                )}
              </div>
              <div className="grid gap-2 mt-6">
                {offer.bookingUrl && (
                  <a href={offer.bookingUrl} target="_blank" rel="noreferrer" data-testid="booking-btn">
                    <Button className="w-full bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
                      <CalendarCheck className="h-4 w-4 mr-2" /> {t('reserve')}
                    </Button>
                  </a>
                )}
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${offer.lat},${offer.lng}`} target="_blank" rel="noreferrer" data-testid="gps-btn">
                  <Button variant="outline" className="w-full border-[#001752] text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200">
                    <Navigation className="h-4 w-4 mr-2" /> {t('gpsNavigate')}
                  </Button>
                </a>
              </div>
            </div>

            <WeatherWidget lat={offer.lat} lng={offer.lng} city={offer.city} />

            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="p-4 pb-0"><h3 className="font-bold text-[#0b1a3d]">{t('location')}</h3></div>
              <div className="p-4">
                <div className="h-64 rounded-lg overflow-hidden" data-testid="detail-map">
                  <MapContainer center={[offer.lat, offer.lng]} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                    <Marker position={[offer.lat, offer.lng]} icon={navyPin} />
                  </MapContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
